import defaultState from './defaultState';
import * as C from '../constants';
import TwentyService from '../../../services/twenty-service';

export default {
  /**
   * @param commit
   */
  resetState({ commit }) {
    commit(C.REVIEW_STATE_RESET, defaultState());
  },

  /**
   * @param commit
   * @param state
   * @returns {Q.Promise<any>}
   */
  fetchReviews({ commit, state }) {
    commit(C.REVIEW_ITEMS_PENDING);
    const offset = state.approval.items.offset;
    const brandIds = state.filter.selectedBrands.map(brand => brand.id);
    const domainIds = state.filter.selectedDomains.map(domain => domain.id);
    const siteIds = state.filter.selectedSites;
    const approvalStatus = state.overview.approvalStatusFilter;
    const sort = state.filter.sort;
    return TwentyService.getReviews(
      offset,
      brandIds,
      domainIds,
      siteIds,
      approvalStatus.toLowerCase(),
      sort,
    )
      .then((res) => {
        commit(C.REVIEW_ITEMS_SUCCESS, res.data);
      })
      .catch(() => {
        commit(C.REVIEW_ITEMS_FAILURE);
      });
  },

  /**
   * @param commit
   * @returns {Q.Promise<never>}
   */
  fetchBrandsAndDomains({ commit }) {
    commit(C.REVIEW_FILTER_ITEMS_PENDING);
    return TwentyService.getBrandsAndDomains()
      .then((res) => {
        commit(C.REVIEW_FILTER_ITEMS_SUCCESS, res.data);
      })
      .catch(() => {
        commit(C.REVIEW_FILTER_ITEMS_FAILURE);
      });
  },

  /**
   * @param commit
   * @returns {Q.Promise<never>}
   */
  fetchSites({ commit }) {
    commit(C.REVIEW_FILTER_SITE_ITEMS_PENDING);
    return TwentyService.getSites()
      .then((res) => {
        commit(C.REVIEW_FILTER_SITE_ITEMS_SUCCESS, res.data);
      })
      .catch(() => {
        commit(C.REVIEW_FILTER_SITE_ITEMS_FAILURE);
      });
  },

  /**
   * @param commit
   * @param payload
   */
  updateSelectedSites({ commit }, payload) {
    commit(C.REVIEW_FILTER_UPDATE_SELECTED_SITES, payload);
    commit(C.REVIEW_FILTER_RESET_REVIEWS);
  },

  updateSort({ commit }, payload) {
    commit(C.REVIEW_FILTER_UPDATE_SORT, payload);
    commit(C.REVIEW_FILTER_RESET_REVIEWS);
  },

  /**
   * @param commit
   * @param state
   * @param payload
   */
  updateSelectedBrands({ commit, state }, payload) {
    const allBrands = state.filter.items;
    const newSelectedBrands = allBrands.filter(brand => payload.includes(brand.name));
    const oldSelectedDomains = state.filter.selectedDomains;
    const oldSelectedDomainIds = oldSelectedDomains.map(domain => domain.id);

    let newSelectedDomains = [];
    if (newSelectedBrands.length) {
      newSelectedBrands.forEach((brand) => {
        const domains = brand.domains.filter(domain => oldSelectedDomainIds.includes(domain.id));
        newSelectedDomains = [
          ...newSelectedDomains,
          ...domains,
        ];
      });
    } else {
      newSelectedDomains = oldSelectedDomains;
    }
    commit(C.REVIEW_FILTER_UPDATE_SELECTED_BRANDS, newSelectedBrands);
    commit(C.REVIEW_FILTER_UPDATE_SELECTED_DOMAINS, newSelectedDomains);
    commit(C.REVIEW_FILTER_RESET_REVIEWS);
  },

  /**
   * @param commit
   * @param state
   * @param payload
   */
  updateSelectedDomains({ commit, state }, payload) {
    const allBrands = state.filter.items;
    let newSelectedDomains = [];
    allBrands.forEach((brand) => {
      const domains = brand.domains.filter(domain => payload.includes(domain.name));
      newSelectedDomains = [
        ...newSelectedDomains,
        ...domains,
      ];
    });
    commit(C.REVIEW_FILTER_UPDATE_SELECTED_DOMAINS, newSelectedDomains);
    commit(C.REVIEW_FILTER_RESET_REVIEWS);
  },

  /**
   * @param state
   * @param dispatch
   * @param index
   */
  removeBrand({ state, dispatch }, index) {
    const selectedBrands = state.filter.selectedBrands;
    selectedBrands.splice(index, 1);
    const mappedSelectedBrands = selectedBrands.map(brand => brand.name);
    dispatch('updateSelectedBrands', mappedSelectedBrands);
  },

  /**
   * @param state
   * @param dispatch
   * @param index
   */
  removeDomain({ state, dispatch }, index) {
    const selectedDomains = state.filter.selectedDomains;
    selectedDomains.splice(index, 1);
    const mappedSelectedDomains = selectedDomains.map(domain => domain.name);
    dispatch('updateSelectedDomains', mappedSelectedDomains);
  },

  /**
   * @param commit
   * @param state
   * @param reviewId
   * @returns {Promise<T | never>}
   */
  approveReview({ commit, state }, reviewId) {
    commit(C.REVIEW_APPROVAL_STATUS_APPROVE_PENDING);
    return TwentyService.approveReviews([reviewId])
      .then(() => {
        let reviews = state.approval.items.reviews;
        reviews = reviews.filter(review => review.reviewId !== reviewId);
        commit(C.REVIEW_APPROVAL_STATUS_APPROVE_SUCCESS, reviews);
      })
      .catch(() => {
        commit(C.REVIEW_APPROVAL_STATUS_APPROVE_FAILURE);
      });
  },

  /**
   * @param commit
   * @param state
   * @param reviewId
   * @returns {Promise<T | never>}
   */
  disapproveReview({ commit, state }, reviewId) {
    commit(C.REVIEW_APPROVAL_STATUS_DISAPPROVE_PENDING);
    return TwentyService.disapproveReviews([reviewId])
      .then(() => {
        let reviews = state.approval.items.reviews;
        reviews = reviews.filter(review => review.reviewId !== reviewId);
        commit(C.REVIEW_APPROVAL_STATUS_DISAPPROVE_SUCCESS, reviews);
      })
      .catch(() => {
        commit(C.REVIEW_APPROVAL_STATUS_DISAPPROVE_FAILURE);
      });
  },

  /**
   * @param commit
   * @param state
   */
  selectAll({ commit, state }) {
    const reviews = state.approval.items.reviews;
    const reviewIds = reviews.map(review => review.reviewId);
    commit(C.REVIEW_BULK_SELECT_ALL, reviewIds);
  },

  /**
   * @param commit
   */
  deselectAll({ commit }) {
    commit(C.REVIEW_BULK_DESELECT_ALL);
  },

  /**
   * @param commit
   * @param state
   * @param reviewId
   */
  updateSelectStatus({ commit, state }, reviewId) {
    const reviewIds = state.bulk.reviewIds;
    if (reviewIds.includes(reviewId)) {
      const index = reviewIds.indexOf(reviewId);
      reviewIds.splice(index, 1);
    } else {
      reviewIds.push(reviewId);
    }
    commit(C.REVIEW_BULK_UPDATE_SELECT, reviewIds);
  },

  /**
   * @param commit
   * @param state
   * @returns {Q.Promise<any>}
   */
  approveReviews({ commit, state }) {
    const reviewIds = state.bulk.reviewIds;
    commit(C.REVIEW_APPROVAL_STATUS_APPROVE_PENDING);
    return TwentyService.approveReviews(reviewIds)
      .then(() => {
        let reviews = state.approval.items.reviews;
        reviews = reviews.filter(review => !reviewIds.includes(review.reviewId));
        commit(C.REVIEW_APPROVAL_STATUS_APPROVE_SUCCESS, reviews);
        commit(C.REVIEW_BULK_DESELECT_ALL);
      })
      .catch(() => {
        commit(C.REVIEW_APPROVAL_STATUS_APPROVE_FAILURE);
      });
  },

  /**
   * @param commit
   * @param state
   * @returns {Q.Promise<any>}
   */
  disapproveReviews({ commit, state }) {
    const reviewIds = state.bulk.reviewIds;
    commit(C.REVIEW_APPROVAL_STATUS_DISAPPROVE_PENDING);
    return TwentyService.disapproveReviews(reviewIds)
      .then(() => {
        let reviews = state.approval.items.reviews;
        reviews = reviews.filter(review => !reviewIds.includes(review.reviewId));
        commit(C.REVIEW_APPROVAL_STATUS_DISAPPROVE_SUCCESS, reviews);
        commit(C.REVIEW_BULK_DESELECT_ALL);
      })
      .catch(() => {
        commit(C.REVIEW_APPROVAL_STATUS_DISAPPROVE_FAILURE);
      });
  },

  /**
   * @param commit
   * @param state
   * @returns {Q.Promise<any>}
   */
  fetchApprovalStatusOverview({ commit, state }) {
    commit(C.REVIEW_OVERVIEW_PENDING);
    const brandIds = state.filter.selectedBrands.map(brand => brand.id);
    const domainIds = state.filter.selectedDomains.map(domain => domain.id);
    const siteIds = state.filter.selectedSites;
    return TwentyService.getApprovalStatusOverview(brandIds, domainIds, siteIds)
      .then((res) => {
        commit(C.REVIEW_OVERVIEW_SUCCESS, res.data);
      })
      .catch(() => {
        commit(C.REVIEW_OVERVIEW_FAILURE);
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param filter
   */
  updateApprovalStatusFilter({ commit, dispatch }, filter) {
    commit(C.REVIEW_OVERVIEW_APPROVAL_STATUS_FILTER, filter);
    commit(C.REVIEW_FILTER_RESET_REVIEWS);
    commit(C.REVIEW_BULK_DESELECT_ALL);
    dispatch('fetchReviews');
  },

  /**
   * @param commit
   * @param state
   * @param payload
   * @returns {Promise<T | never>}
   */
  updateReviewInformationText({ commit, state }, payload) {
    commit(C.REVIEW_UPDATE_REVIEW_INFORMATION_TEXT_PENDING);
    const reviewId = payload.shift().reviewId;
    return TwentyService.updateReviewInformationText(payload)
      .then(() => {
        const reviews = state.approval.items.reviews;
        reviews.forEach((element) => {
          if (element.reviewId === reviewId) {
            element.reviewInformation.forEach((reviewInformation) => {
              payload.forEach((load) => {
                if (reviewInformation.id === load.reviewInformationId) {
                  // eslint-disable-next-line no-param-reassign
                  reviewInformation.text = load.reviewInformationText;
                }
                return reviewInformation;
              });
            });
          }
          return element;
        });
        commit(C.REVIEW_UPDATE_REVIEW_INFORMATION_TEXT_SUCCESS, reviews);
      })
      .catch(() => {
        commit(C.REVIEW_UPDATE_REVIEW_INFORMATION_TEXT_FAILURE);
      });
  },

};



// WEBPACK FOOTER //
// ./src/store/modules/review/actions.js